import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  ChartCard,
  ChartData,
  DataArrays,
  IntAthenaType,
  MetadataColumn,
  StringAndDateAthenaType,
} from '../../models/charts';
import { ChartDataService } from '../../services/chart-data.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map, Subject, takeUntil } from 'rxjs';
import { ChartPreviewComponent } from '../chart-preview/chart-preview.component';
import { ApiService } from '../../../shared/services/api.service';
import {
  ChartMetadata,
  ChartType,
  CreateChartsMutation,
  CreateChartsMutationVariables,
  Engagements,
  GetPreSignedUrlQuery,
  GetPreSignedUrlQueryVariables,
  PreSignedUrl,
  SaveChartInput,
} from '../../../../API';
import { GetPreSignedUrl } from '../../../../graphql/queries';
import { CreateCharts } from '../../../../graphql/mutations';
import { v4 as uuidv4 } from 'uuid';
import { AppService } from '../../../shared/services/app.service';
import { S3StorageService } from '../../../shared/services/s3-storage.service';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { Router } from '@angular/router';
import { COLOR_PALETTES, US_STATE_MAPPINGS, chartCardList } from '../../models/chart-data';

@Component({
  selector: 'app-chart-config',
  templateUrl: './chart-config.component.html',
  styleUrl: './chart-config.component.scss',
})
export class ChartConfigComponent implements OnInit, OnDestroy {
  private activeChart$ = new BehaviorSubject<ChartCard | null | undefined>(null);

  @Input()
  set selectedChart(card: ChartCard | null | undefined) {
    if (card) {
      this.activeChart$.next(card);
    }
  }

  @Input() chartDetailsForm?: FormGroup;

  @Output() backEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() typeAvailability: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() chartCardsValidated: EventEmitter<ChartCard[]> = new EventEmitter<ChartCard[]>();
  @ViewChild(ChartPreviewComponent) chartComp!: ChartPreviewComponent;
  private destroy$ = new Subject<void>();

  engagement!: string;
  username!: string;
  orgType!: string;
  activeChart: ChartCard | null | undefined = null;
  chartConfigForm?: FormGroup;
  palettes = COLOR_PALETTES;

  chartData?: ChartData;
  categoricalCols: string[] = [];
  numericCols: string[] = [];
  scatterChartColsOptions: string[] = [];
  chartPreviewData?: DataArrays;
  chartMetadata: ChartMetadata | null = null;
  isEditMode: boolean = false;
  isViewMode: boolean = false;

  constructor(
    private fb: FormBuilder,
    public chartDataService: ChartDataService,
    private nzMessageService: NzMessageService,
    private apiService: ApiService,
    private appService: AppService,
    private s3Service: S3StorageService,
    private userManagementService: UserManagementService,
    private router: Router,
  ) {
    this.username = this.userManagementService.getEmail();
    this.orgType = this.userManagementService.getOrgType();
  }

  ngOnInit(): void {
    this.appService.activeEngagement$.pipe(takeUntil(this.destroy$)).subscribe((engagement: Engagements) => {
      this.engagement = engagement.engagement;
    });

    this.chartMetadata = this.chartDataService.chartMetadata;
    this.isEditMode = !!this.chartMetadata;
    this.isViewMode = this.chartDataService.isViewMode;

    if (!this.isEditMode) {
      this.chartDataService.chartData$
        .pipe(
          filter((data) => !!data),
          takeUntil(this.destroy$),
        )
        .subscribe((chartData) => {
          if (chartData) {
            chartCardList.map((card: ChartCard) => {
              this.buildChartDataConfig(chartData, card);
            });
            this.chartCardsValidated.emit(chartCardList);
          }
        });
    }

    combineLatest([this.chartDataService.chartData$, this.activeChart$.pipe(filter((c): c is ChartCard => !!c))])
      .pipe(
        map(([data, chart]) => {
          if (!data || !chart) {
            return {
              activeChart: chart ?? null,
              chartData: data ?? null,
              categoricalCols: [],
              numericCols: [],
            };
          }
          return this.buildChartDataConfig(data, chart);
        }),
        distinctUntilChanged((prev, curr) => prev?.activeChart?.type === curr?.activeChart?.type),
        takeUntil(this.destroy$),
      )
      .subscribe((config) => {
        this.activeChart = !config?.activeChart.disabled ? config?.activeChart : null;
        this.chartData = config?.chartData;
        this.categoricalCols = config?.categoricalCols || [];
        this.numericCols = config?.numericCols || [];
        this.scatterChartColsOptions = config?.numericCols || [];

        this.initiateForm();

        this.chartConfigForm?.get('xAxisValue')?.valueChanges.subscribe((newValue) => {
          if (this.activeChart?.type === ChartType.SCATTER) {
            this.numericCols = this.scatterChartColsOptions.filter((value) => value != newValue);
          }
        });

        this.chartConfigForm?.get('yAxisValue')?.valueChanges.subscribe((newValue) => {
          if (this.activeChart?.type === ChartType.SCATTER) {
            this.categoricalCols = this.scatterChartColsOptions.filter((value) => value != newValue);
          }
        });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    chartCardList.forEach((chart: ChartCard) => {
      chart.disabled = false;
      chart.tooltip = undefined;
    });
  }

  buildChartDataConfig(chartData: ChartData, activeChart: ChartCard) {
    let categoricalCols: string[] = [];
    const numericCols: string[] = [];
    const metadata = JSON.parse(chartData?.type || {});
    const isTypeAvailable = metadata?.columnsList?.every((col: MetadataColumn) => col.type);
    if (!isTypeAvailable) {
      this.typeAvailability.emit(false);
      activeChart.disabled = true;
      activeChart.tooltip = 'Data type for one or more column is not available from query result';
      return;
    }
    this.typeAvailability.emit(true);

    metadata?.columnsList?.forEach((col: MetadataColumn) => {
      if (col?.type && StringAndDateAthenaType.includes(col?.type)) {
        categoricalCols.push(col.name);
      } else if (col?.type && IntAthenaType.includes(col?.type)) {
        numericCols.push(col.name);
      }
    });
    this.chartPreviewData = chartData.data.reduce((acc, obj) => {
      Object.entries(obj).forEach(([key, value]) => {
        if (!acc[key]) acc[key] = [];
        acc[key].push(value);
      });
      return acc;
    }, {});

    if (this.isEditMode) return { activeChart, chartData, categoricalCols, numericCols };

    if (activeChart?.type === ChartType.SCATTER) {
      if (numericCols.length <= 1) {
        activeChart.disabled = true;
        activeChart.tooltip = `Requires at least 2 numeric fields for scatter chart`;
        return { activeChart, chartData, categoricalCols: [], numericCols: [] };
      }
      categoricalCols = [...numericCols];
      activeChart.disabled = false;
      activeChart.tooltip = undefined;
    }
    if (activeChart?.type === ChartType.GEO) {
      if (categoricalCols.length < 1) {
        activeChart.disabled = true;
        activeChart.tooltip = `No valid state data for geo chart`;
        return { activeChart, chartData, categoricalCols: [], numericCols: [] };
      }

      const hasValidStateData = this.validateStateData(chartData, categoricalCols);
      if (!hasValidStateData) {
        activeChart.disabled = true;
        activeChart.tooltip = 'No valid US state data found. Data should contain US state names or abbreviations.';
        return { activeChart, chartData, categoricalCols: [], numericCols: [] };
      }

      activeChart.disabled = false;
      activeChart.tooltip = undefined;
    } else {
      if (categoricalCols.length < 1) {
        activeChart.disabled = true;
        activeChart.tooltip = `Requires at least 1 categorical field for ${activeChart?.type?.toLowerCase()} chart`;
        return { activeChart, chartData, categoricalCols: [], numericCols: [] };
      }
    }

    return { activeChart, chartData, categoricalCols, numericCols };
  }

  private validateStateData(chartData: ChartData, categoricalCols: string[]): boolean {
    const stateAbbreviations = Object.keys(US_STATE_MAPPINGS).map((key) => key.toUpperCase());
    const stateFullNames = Object.values(US_STATE_MAPPINGS).map((name) => (name as string).toLowerCase());

    return categoricalCols.some((colName) => {
      const columnData = chartData.data.map((row) => row[colName]);

      return columnData.some((value) => {
        if (!value || typeof value !== 'string') return false;

        const normalizedValue = value.toString().trim();

        if (stateAbbreviations.includes(normalizedValue.toUpperCase())) {
          return true;
        }

        if (stateFullNames.includes(normalizedValue.toLowerCase())) {
          return true;
        }

        const titleCaseValue = normalizedValue.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
        if (stateFullNames.includes(titleCaseValue.toLowerCase())) {
          return true;
        }

        return false;
      });
    });
  }

  initiateForm() {
    const config = this.activeChart?.displayConfig;

    let existingConfig: Record<string, unknown> | null = null;

    if (this.chartMetadata?.config) {
      try {
        existingConfig = JSON.parse(this.chartMetadata.config);
      } catch (error) {
        console.error('Error parsing chart configuration:', error);
      }
    }

    this.chartConfigForm = this.fb.group({
      chartTitle: this.fb.control(existingConfig?.['chartTitle'] || '', [Validators.maxLength(30)]),
      xAxisValue: this.fb.control(
        existingConfig?.['xAxisValue'] || '',
        config?.xAxisValue ? [Validators.required] : null,
      ),
      yAxisValue: this.fb.control(
        existingConfig?.['yAxisValue'] || '',
        config?.yAxisValue ? [Validators.required] : null,
      ),
      xAxisLabel: this.fb.control(existingConfig?.['xAxisLabel'] || '', [Validators.maxLength(30)]),
      yAxisLabel: this.fb.control(existingConfig?.['yAxisLabel'] || '', [Validators.maxLength(30)]),
      chartColor: this.fb.control(existingConfig?.['chartColor'] || ''),
      barWidth: this.fb.control(existingConfig?.['barWidth'] || '50'),
      xAxisGridlines: this.fb.control(
        existingConfig?.['xAxisGridlines'] !== undefined ? existingConfig['xAxisGridlines'] : true,
      ),
      yAxisGridlines: this.fb.control(
        existingConfig?.['yAxisGridlines'] !== undefined ? existingConfig['yAxisGridlines'] : true,
      ),
      pointSize: this.fb.control(existingConfig?.['pointSize'] || '10'),
      category: this.fb.control(existingConfig?.['category'] || '', config?.category ? [Validators.required] : null),
      categoryValue: this.fb.control(
        existingConfig?.['categoryValue'] || '',
        config?.categoryValue ? [Validators.required] : null,
      ),
      location: this.fb.control(existingConfig?.['location'] || '', config?.location ? [Validators.required] : null),
      locationValue: this.fb.control(
        existingConfig?.['locationValue'] || '',
        config?.locationValue ? [Validators.required] : null,
      ),
      colorPalette: this.fb.control(existingConfig?.['colorPalette'] || 1),
      mapType: this.fb.control(
        existingConfig?.['mapType'] || 'USA States',
        config?.mapType ? [Validators.required] : null,
      ),
    });
  }

  back(): void {
    if (!this.isEditMode) return this.backEvent.emit();
    this.router.navigate(['/bi/manage-charts']);
  }

  saveChart() {
    this.chartDataService.isSaveChartLoading = true;
    this.chartComp.generateChartImage(this.activeChart!.type);
  }

  async uploadAndSaveChart(image: string): Promise<void> {
    const chartId = uuidv4();

    const input: PreSignedUrl = {
      contentType: 'image/svg+xml',
      bucketSuffix: 'bi-image-storage',
      s3keyName: `images/chart/${this.engagement}/${chartId}/chart.svg`,
    };

    try {
      const { data } = await this.apiService.query<GetPreSignedUrlQuery, GetPreSignedUrlQueryVariables>(
        GetPreSignedUrl,
        { input },
      );

      const preSignedUrlResponse = data?.GetPreSignedUrl;
      if (
        !preSignedUrlResponse ||
        preSignedUrlResponse.statusCode !== 200 ||
        !preSignedUrlResponse.putUrl ||
        !preSignedUrlResponse.deleteUrl
      ) {
        this.nzMessageService.error('Failed to get pre-signed URL, please try again');
        this.chartDataService.isSaveChartLoading = false;
        return;
      }

      const { putUrl, deleteUrl } = preSignedUrlResponse;

      await this.s3Service.uploadSvg(putUrl, image);

      const s3Url = putUrl.split('?')[0];

      // Save chart metadata
      try {
        await this.createChartMetadata(chartId, s3Url);
      } catch (metadataError) {
        // If metadata saving fails, clean up the uploaded image
        try {
          await fetch(deleteUrl, { method: 'DELETE' });
          console.log('Cleaned up uploaded image due to metadata save failure');
        } catch (deleteError) {
          console.error('Failed to clean up image after metadata save failure:', deleteError);
        }
        throw metadataError;
      }
    } catch (error: unknown) {
      this.nzMessageService.error((error as Error)?.message ?? 'Unexpected error while saving chart');
      console.error('Error in uploadAndSaveChart:', error);
    } finally {
      this.chartDataService.isSaveChartLoading = false;
    }
  }

  async createChartMetadata(chartId: string, imageUrl: string): Promise<void> {
    if (!this.chartDetailsForm || !this.chartConfigForm || !this.activeChart) {
      this.nzMessageService.error('Chart forms or active chart not available');
      this.chartDataService.isSaveChartLoading = false;
      return;
    }

    const saveChartInput: SaveChartInput = {
      chartId: this.isEditMode ? this.chartMetadata?.chartId : chartId,
      ...this.chartDetailsForm.value,
      chartType: this.activeChart.type as ChartType,
      chartsImageUrl: imageUrl,
      config: JSON.stringify(this.chartConfigForm.value),
      createdBy: this.isEditMode ? this.chartMetadata?.createdBy : this.username,
      engagement: this.engagement,
      lastModifiedBy: this.username,
      orgType: this.orgType,
    };

    try {
      const { data } = await this.apiService.query<CreateChartsMutation, CreateChartsMutationVariables>(CreateCharts, {
        input: saveChartInput,
      });

      if (data?.CreateCharts) {
        const actionText = this.isEditMode ? 'updated' : 'saved';
        this.nzMessageService.success(`Chart ${actionText} successfully!`);
        this.goToManageCharts();
      } else {
        this.nzMessageService.error('Failed to save chart metadata');
      }
    } catch (error: unknown) {
      console.error('Error in createChartMetadata:', error);
      this.nzMessageService.error((error as Error)?.message ?? 'Unexpected error while saving chart');
      throw error;
    }
  }

  goToManageCharts() {
    this.router.navigate(['/bi/manage-charts']);
  }

  get saveButtonText(): string {
    if (this.chartDataService.isSaveChartLoading) {
      return this.isEditMode ? 'Updating...' : 'Saving...';
    }
    return this.isEditMode ? 'Update' : 'Save';
  }

  get shouldShowChartPreview(): boolean {
    if (!this.chartPreviewData || !this.chartConfigForm) {
      return false;
    }
    return this.chartConfigForm.valid;
  }

  getChartImage(): string {
    return `${this.appService.domainUrl}/images/chart/${this.chartMetadata?.engagement}/${this.chartMetadata?.chartId}/chart.png`;
  }

  get isChartPreviewAvailable(): boolean {
    return (this.chartPreviewData && Object.keys(this.chartPreviewData).length > 0) || false;
  }
}
